import { Skeleton } from '../../components/shadcn/index.js';

/**
 * Layout-matching loading skeletons for the public storefront. Each mirrors the
 * structure of its page so the shell doesn't jump when real data arrives.
 */

/** A grid of product-card skeletons (image + name + subtitle + price). */
export function ProductGridSkeleton({ count = 8 }: { count?: number }): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex flex-col overflow-hidden rounded-2xl border border-border bg-bg-subtle">
          <Skeleton className="aspect-square w-full rounded-none" />
          <div className="flex flex-col gap-2 p-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-5 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Home page: hero + shop-by-category tiles + featured grid. */
export function HomeSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-12">
      <Skeleton className="h-64 w-full rounded-3xl sm:h-80" />
      <div className="flex flex-col gap-5">
        <Skeleton className="h-7 w-48" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="aspect-[4/3] w-full rounded-2xl" />)}
        </div>
      </div>
      <div className="flex flex-col gap-5">
        <Skeleton className="h-7 w-44" />
        <ProductGridSkeleton count={4} />
      </div>
    </div>
  );
}

/** Product detail: gallery (image + thumbs) + info column + accordions. */
export function ProductDetailSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-4 w-72" />
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div className="flex flex-col gap-3">
          <Skeleton className="aspect-square w-full rounded-xl" />
          <div className="flex gap-2">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-16 rounded-md" />)}
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-7 w-32" />
          <div className="flex gap-3 pt-2">
            <Skeleton className="h-12 w-32 rounded-lg" />
            <Skeleton className="h-12 flex-1 rounded-md" />
          </div>
          <Skeleton className="mt-2 h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
        </div>
      </div>
    </div>
  );
}

/** Category landing: hero banner + product grid. */
export function CategorySkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-8">
      <Skeleton className="h-4 w-64" />
      <Skeleton className="h-48 w-full rounded-3xl sm:h-56" />
      <ProductGridSkeleton count={8} />
    </div>
  );
}

/** Order detail: heading + lines table + totals. */
export function OrderSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-6">
      <Skeleton className="h-7 w-40" />
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-48 w-full rounded-2xl" />
      <div className="ms-auto flex w-full max-w-xs flex-col gap-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-5 w-2/3" />
      </div>
    </div>
  );
}
